/**
 * Table editing UX extension — adds row/col insert/delete controls, cell selection
 * decorations, column resize tracking, and horizontal overflow handling to tiptap's
 * base Table extension. Rendered as a ProseMirror plugin with DOM overlays.
 *
 * Self-contained — references only tiptap + the ProseMirror types, so safe to drop
 * into any editor that uses the Table extensions.
 */
import type { Editor } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import type { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

// Pixel constants shared across plugins — pulled out so the 3+ sites that
// reference them stay in lockstep.
const DEFAULT_NEW_COL_WIDTH_PX = 150;
const CELL_EDGE_HIT_PX = 12;
const HOVER_BTN_OFFSET_PX = 4;
const DOT_HALF_PX = 5;
const REPOSITION_TOLERANCE_PX = 0.5;
const SEL_EDGE_ALIGN_TOLERANCE_PX = 2;

const getEditorOverlayContainer = (editorDom: HTMLElement) => editorDom.closest<HTMLElement>('.notion-editor-wrapper');

const getEditorScrollContainer = (editorDom: HTMLElement): HTMLElement | null =>
  editorDom.closest('.overflow-y-auto, .overflow-auto') as HTMLElement | null;

/**
 * The overlay is a sibling of the table wrapper rather than a child, so nothing clips it to the
 * wrapper's rounded corners — a selection reaching one would sit proud of the curve.
 */
const selectionOverlayRadii = (sel: DOMRect, wrapper: HTMLElement | null, fallback: string) => {
  const radius = wrapper && getComputedStyle(wrapper).getPropertyValue('--notion-table-radius').trim();
  const table = wrapper?.querySelector('table')?.getBoundingClientRect();
  if (!radius || !table) return fallback;
  // Against the table rather than the wrapper: a scrolled table's clipped edge isn't a corner.
  const near = (a: number, b: number) => Math.abs(a - b) <= SEL_EDGE_ALIGN_TOLERANCE_PX;
  const [top, bottom, left, right] = [
    near(sel.top, table.top),
    near(sel.bottom, table.bottom),
    near(sel.left, table.left),
    near(sel.right, table.right),
  ];
  return [top && left, top && right, bottom && right, bottom && left]
    .map(corner => (corner ? radius : fallback))
    .join(' ');
};

/** Factory: viewport coords → overlay-container-relative absolute coords. */
const makeToAbs = (container: HTMLElement) => (vx: number, vy: number) => {
  const sr = container.getBoundingClientRect();
  return { top: vy - sr.top + container.scrollTop, left: vx - sr.left + container.scrollLeft };
};
type ToAbs = ReturnType<typeof makeToAbs>;

/**
 * tiptap builds plugin views before the editor is on the page, and a read-only editor never rebuilds
 * them, so the controls mount here, on each positioning call. On `document.body` they'd sit in page
 * coordinates and stretch the page. A read-only editor gets none: every control edits the table.
 */
const mountInEditorWrapper = (view: EditorView, controls: HTMLElement[]) => {
  if (!view.editable) return null;
  const wrapper = getEditorOverlayContainer(view.dom);
  if (!wrapper) return null;
  for (const el of controls) if (el.parentElement !== wrapper) wrapper.appendChild(el);
  return makeToAbs(wrapper);
};

/**
 * Union bounding rect of the current cell selection (or the active-cell
 * decoration when the user just has a cursor in a cell). Returns null when
 * there's no table cell in play.
 */
const getCellSelectionBounds = (view: EditorView): DOMRect | null => {
  const sel = view.state.selection;
  if (sel instanceof CellSelection) {
    const cells = view.dom.querySelectorAll('.selectedCell');
    if (cells.length === 0) return null;
    let top = Infinity;
    let left = Infinity;
    let bottom = -Infinity;
    let right = -Infinity;
    cells.forEach(c => {
      const cr = c.getBoundingClientRect();
      top = Math.min(top, cr.top);
      left = Math.min(left, cr.left);
      bottom = Math.max(bottom, cr.bottom);
      right = Math.max(right, cr.right);
    });
    return new DOMRect(left, top, right - left, bottom - top);
  }
  const ac = view.dom.querySelector('[data-active-cell]') as HTMLElement | null;
  return ac ? ac.getBoundingClientRect() : null;
};

/** Resolve the position of the table cell at viewport coords `(x, y)`. */
const findCellAtCoords = (view: EditorView, x: number, y: number): number | null => {
  const posInfo = view.posAtCoords({ left: x, top: y });
  if (!posInfo) return null;
  const $pos = view.state.doc.resolve(posInfo.pos);
  for (let d = $pos.depth; d > 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === 'tableCell' || name === 'tableHeader') return $pos.before(d);
  }
  return null;
};

/** Put the cursor back inside (rowIdx, colIdx) after a row/col mutation. */
const restoreCursorInTable = (editor: Editor, table: HTMLElement, rowIdx: number, colIdx: number) => {
  try {
    const targetRow = table.querySelectorAll('tr')[rowIdx];
    const targetCell = targetRow?.children[colIdx];
    if (targetCell) {
      const pos = editor.view.posAtDOM(targetCell, 0);
      editor.commands.setTextSelection(pos);
    }
  } catch {
    /* ignore */
  }
};

export const TableControls = Extension.create({
  name: 'tableControls',
  addProseMirrorPlugins() {
    const editor = this.editor;

    // Shared handle for any in-flight cell-drag interaction so the plugin's
    // view destroy can tear down document-level mousemove/mouseup listeners
    // if the editor is destroyed mid-drag.
    let activeCellDragCleanup: (() => void) | null = null;
    const registerCellDragCleanup = (cleanup: () => void) => {
      activeCellDragCleanup?.();
      activeCellDragCleanup = cleanup;
    };
    const clearCellDragCleanup = (cleanup: () => void) => {
      if (activeCellDragCleanup === cleanup) activeCellDragCleanup = null;
    };

    const captureColWidths = (wrapper: HTMLElement): number[] => {
      const firstRow = wrapper.querySelector('table tr');
      if (!firstRow) return [];
      return Array.from(firstRow.children).map(cell => (cell as HTMLElement).offsetWidth);
    };

    const applyColWidths = (widths: number[]) => {
      const { $from } = editor.state.selection;
      let tableDepth = -1;
      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type.name === 'table') {
          tableDepth = d;
          break;
        }
      }
      if (tableDepth < 0) return;

      const tableStart = $from.before(tableDepth);
      const tableEnd = $from.after(tableDepth);
      const { tr } = editor.state;
      let changed = false;
      let colIdx = 0;

      editor.state.doc.nodesBetween(tableStart, tableEnd, (node, pos) => {
        if (node.type.name === 'table') return true;
        if (node.type.name === 'tableRow') {
          colIdx = 0;
          return true;
        }
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          if (colIdx < widths.length) {
            const cw = node.attrs.colwidth;
            if (!cw || cw[0] !== widths[colIdx]) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, colwidth: [widths[colIdx]] });
              changed = true;
            }
          }
          colIdx++;
          return false;
        }
        return true;
      });

      if (changed) editor.view.dispatch(tr);
    };

    return [
      new Plugin({
        key: new PluginKey('tableControls'),
        view() {
          const scrollTarget = getEditorScrollContainer(editor.view.dom) ?? document;

          const colBtn = document.createElement('button');
          colBtn.type = 'button';
          colBtn.className = 'table-add-col-btn';
          colBtn.textContent = '+';

          const rowBtn = document.createElement('button');
          rowBtn.type = 'button';
          rowBtn.className = 'table-add-row-btn';
          rowBtn.textContent = '+';
          mountInEditorWrapper(editor.view, [colBtn, rowBtn]);

          let activeWrapper: HTMLElement | null = null;

          const getCursorCellInfo = (wrapper: HTMLElement) => {
            const { $from } = editor.state.selection;
            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
                const cellDom = editor.view.nodeDOM($from.before(d));
                if (cellDom instanceof HTMLElement && wrapper.contains(cellDom)) {
                  const row = cellDom.parentElement;
                  const table = cellDom.closest('table');
                  if (row && table) {
                    return {
                      table,
                      rowIdx: Array.from(table.querySelectorAll('tr')).indexOf(row as HTMLTableRowElement),
                      colIdx: Array.from(row.children).indexOf(cellDom),
                    };
                  }
                }
                break;
              }
            }
            return null;
          };

          const focusLastCell = (wrapper: HTMLElement, lastCol = false) => {
            const cells = wrapper.querySelectorAll(
              lastCol
                ? 'tr:first-child td:last-child, tr:first-child th:last-child'
                : 'tr:last-child td:last-child, tr:last-child th:last-child',
            );
            const cell = cells[cells.length - 1];
            if (cell) {
              const pos = editor.view.posAtDOM(cell, 0);
              editor.commands.setTextSelection(pos);
            }
          };

          const restoreCursor = (table: HTMLElement, rowIdx: number, colIdx: number) =>
            restoreCursorInTable(editor, table, rowIdx, colIdx);

          // Move selection out of the table (to after the table wrapper).
          // Walk $pos.depth until we find the table node — hardcoding depth 1
          // silently mis-positions when the table sits under a list, blockquote,
          // or any other wrapper block.
          const blurTable = (wrapper: HTMLElement) => {
            try {
              const pos = editor.view.posAtDOM(wrapper, 0);
              const $pos = editor.state.doc.resolve(pos);
              for (let d = $pos.depth; d > 0; d--) {
                if ($pos.node(d).type.name === 'table') {
                  editor.commands.setTextSelection($pos.after(d));
                  return;
                }
              }
            } catch {
              /* ignore */
            }
          };

          colBtn.addEventListener('mousedown', e => {
            e.preventDefault();
            if (!activeWrapper) return;
            const savedWidths = captureColWidths(activeWrapper);
            const info = getCursorCellInfo(activeWrapper);
            focusLastCell(activeWrapper, true);
            editor.chain().addColumnAfter().run();
            applyColWidths([...savedWidths, DEFAULT_NEW_COL_WIDTH_PX]);
            if (info) {
              restoreCursor(info.table, info.rowIdx, info.colIdx);
            } else {
              blurTable(activeWrapper);
            }
          });

          rowBtn.addEventListener('mousedown', e => {
            e.preventDefault();
            if (!activeWrapper) return;
            const info = getCursorCellInfo(activeWrapper);
            focusLastCell(activeWrapper);
            editor.chain().addRowAfter().run();
            if (info) {
              restoreCursor(info.table, info.rowIdx, info.colIdx);
            } else {
              blurTable(activeWrapper);
            }
          });

          let btnRafId = 0;
          const findWrapperAt = (x: number, y: number): HTMLElement | null => {
            const els = document.elementsFromPoint(x, y);
            for (const el of els) {
              const w = (el as HTMLElement).closest?.('.tableWrapper') as HTMLElement | null;
              if (w) return w;
            }
            return null;
          };

          const onMouseMove = (e: MouseEvent) => {
            // Read off the event synchronously: by the time the frame runs the browser may have
            // recycled it, leaving `target` null and the coordinates stale.
            const target = e.target as HTMLElement | null;
            const { clientX, clientY } = e;
            cancelAnimationFrame(btnRafId);
            btnRafId = requestAnimationFrame(() => {
              const toAbs = mountInEditorWrapper(editor.view, [colBtn, rowBtn]);
              if (!toAbs) return;
              if (editor.view.dom.hasAttribute('data-cell-selecting')) {
                colBtn.classList.remove('visible');
                rowBtn.classList.remove('visible');
                activeWrapper = null;
                return;
              }
              const isOnBtn = target?.closest?.('.table-add-col-btn') || target?.closest?.('.table-add-row-btn');
              const wrapper = findWrapperAt(clientX, clientY) ?? findWrapperAt(clientX, clientY - 20);
              if (wrapper) {
                activeWrapper = wrapper;
                const table = wrapper.querySelector('table');
                const r = table ? table.getBoundingClientRect() : wrapper.getBoundingClientRect();
                const distRight = Math.abs(clientX - r.right);
                const distBottom = Math.abs(clientY - r.bottom);
                const showCol = distRight < distBottom;

                const wr = wrapper.getBoundingClientRect();
                if (showCol) {
                  const clippedRight = Math.min(r.right, wr.right);
                  const a = toAbs(clippedRight + HOVER_BTN_OFFSET_PX, r.top);
                  colBtn.style.top = `${a.top}px`;
                  colBtn.style.left = `${a.left}px`;
                  colBtn.style.height = `${r.height}px`;
                  const clipTop = Math.max(0, wr.top - r.top);
                  const clipBottom = Math.max(0, r.bottom - wr.bottom);
                  colBtn.style.clipPath =
                    clipTop > 0 || clipBottom > 0 ? `inset(${clipTop}px 0 ${clipBottom}px 0)` : '';
                  colBtn.classList.add('visible');
                  rowBtn.classList.remove('visible');
                } else {
                  const clippedLeft = Math.max(r.left, wr.left);
                  const clippedRight = Math.min(r.right, wr.right);
                  const a = toAbs(clippedLeft, r.bottom + HOVER_BTN_OFFSET_PX);
                  rowBtn.style.top = `${a.top}px`;
                  rowBtn.style.left = `${a.left}px`;
                  rowBtn.style.width = `${clippedRight - clippedLeft}px`;
                  rowBtn.classList.add('visible');
                  colBtn.classList.remove('visible');
                }
              } else if (!isOnBtn) {
                colBtn.classList.remove('visible');
                rowBtn.classList.remove('visible');
                activeWrapper = null;
              }
            });
          };

          document.addEventListener('mousemove', onMouseMove);

          let lastClientX = -1;
          let lastClientY = -1;
          const trackPointer = (e: MouseEvent) => {
            lastClientX = e.clientX;
            lastClientY = e.clientY;
          };
          document.addEventListener('mousemove', trackPointer);

          let scrollRafId = 0;
          const onScroll = () => {
            if (lastClientX < 0) return;
            cancelAnimationFrame(scrollRafId);
            scrollRafId = requestAnimationFrame(() => {
              onMouseMove({
                clientX: lastClientX,
                clientY: lastClientY,
                target: document.elementFromPoint(lastClientX, lastClientY),
              } as unknown as MouseEvent);
            });
          };
          scrollTarget.addEventListener('scroll', onScroll, true);

          return {
            update() {},
            destroy() {
              cancelAnimationFrame(btnRafId);
              cancelAnimationFrame(scrollRafId);
              colBtn.remove();
              rowBtn.remove();
              document.removeEventListener('mousemove', onMouseMove);
              document.removeEventListener('mousemove', trackPointer);
              scrollTarget.removeEventListener('scroll', onScroll, true);
            },
          };
        },
      }),
      // Track active cell decoration (single cursor inside a cell)
      new Plugin({
        key: new PluginKey('activeCellTracker'),
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(_tr, _old, _oldState, newState) {
            if (newState.selection instanceof CellSelection) return DecorationSet.empty;
            const { $from } = newState.selection;
            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
                const pos = $from.before(d);
                return DecorationSet.create(newState.doc, [
                  Decoration.node(pos, pos + node.nodeSize, { 'data-active-cell': '' }),
                ]);
              }
            }
            return DecorationSet.empty;
          },
        },
        props: {
          decorations(state) {
            return editor.isEditable ? this.getState(state) : DecorationSet.empty;
          },
        },
      }),
      // Unified cell selection UI: edge cursors, drag-dot, overlay border
      new Plugin({
        key: new PluginKey('cellSelectionUI'),
        props: {
          handleDOMEvents: {
            mousemove(view, event) {
              const target = event.target as HTMLElement;
              const cell = target.closest('td, th') as HTMLElement | null;
              if (!cell || !target.closest('table')) {
                view.dom.removeAttribute('data-cell-cursor');
                return false;
              }
              const rect = cell.getBoundingClientRect();
              const x = event.clientX;
              const y = event.clientY;
              const nearTop = y - rect.top < CELL_EDGE_HIT_PX;
              const nearBottom = rect.bottom - y < CELL_EDGE_HIT_PX;
              const nearLeft = x - rect.left < CELL_EDGE_HIT_PX;
              const nearRight = rect.right - x < CELL_EDGE_HIT_PX;
              if (!nearTop && !nearBottom && !nearLeft && !nearRight) {
                view.dom.removeAttribute('data-cell-cursor');
                return false;
              }
              const selRect = getCellSelectionBounds(view);
              let cursor = '';
              if (selRect) {
                const T = SEL_EDGE_ALIGN_TOLERANCE_PX;
                const atSelTop =
                  nearTop && Math.abs(rect.top - selRect.top) < T && x >= selRect.left - T && x <= selRect.right + T;
                const atSelBottom =
                  nearBottom &&
                  Math.abs(rect.bottom - selRect.bottom) < T &&
                  x >= selRect.left - T &&
                  x <= selRect.right + T;
                const atSelLeft =
                  nearLeft && Math.abs(rect.left - selRect.left) < T && y >= selRect.top - T && y <= selRect.bottom + T;
                const atSelRight =
                  nearRight &&
                  Math.abs(rect.right - selRect.right) < T &&
                  y >= selRect.top - T &&
                  y <= selRect.bottom + T;
                const atSelEdge = atSelTop || atSelBottom || atSelLeft || atSelRight;
                if (!atSelEdge) {
                  view.dom.removeAttribute('data-cell-cursor');
                  return false;
                }
                const atSelCorner = (atSelTop || atSelBottom) && (atSelLeft || atSelRight);
                if (view.dom.classList.contains('resize-cursor') && !atSelCorner) {
                  view.dom.removeAttribute('data-cell-cursor');
                  return false;
                }
                if (atSelCorner) {
                  const isAbove = y < selRect.top + selRect.height / 2;
                  const isLeft = x < selRect.left + selRect.width / 2;
                  cursor = isAbove === isLeft ? 'nwse-resize' : 'nesw-resize';
                } else if (atSelTop || atSelBottom) {
                  cursor = 'ns-resize';
                } else {
                  cursor = 'ew-resize';
                }
              } else {
                if (view.dom.classList.contains('resize-cursor')) {
                  view.dom.removeAttribute('data-cell-cursor');
                  return false;
                }
                const cellCorner = (nearTop || nearBottom) && (nearLeft || nearRight);
                if (cellCorner) {
                  if ((nearTop && nearLeft) || (nearBottom && nearRight)) {
                    cursor = 'nwse-resize';
                  } else {
                    cursor = 'nesw-resize';
                  }
                } else if (nearTop || nearBottom) {
                  cursor = 'ns-resize';
                } else {
                  cursor = 'ew-resize';
                }
              }
              view.dom.setAttribute('data-cell-cursor', cursor);
              return false;
            },
            mousedown(view, event) {
              const target = event.target as HTMLElement;
              const cell = target.closest('td, th') as HTMLElement | null;
              if (!cell) return false;
              const rect = cell.getBoundingClientRect();
              const x = event.clientX;
              const y = event.clientY;
              const nearTop = y - rect.top < CELL_EDGE_HIT_PX;
              const nearBottom = rect.bottom - y < CELL_EDGE_HIT_PX;
              const nearLeft = x - rect.left < CELL_EDGE_HIT_PX;
              const nearRight = rect.right - x < CELL_EDGE_HIT_PX;
              if (!nearTop && !nearBottom && !nearLeft && !nearRight) return false;

              const sel = view.state.selection;
              const selRect = getCellSelectionBounds(view);
              if (!selRect) return false;
              const T = SEL_EDGE_ALIGN_TOLERANCE_PX;
              const atSelTop =
                nearTop && Math.abs(rect.top - selRect.top) < T && x >= selRect.left - T && x <= selRect.right + T;
              const atSelBottom =
                nearBottom &&
                Math.abs(rect.bottom - selRect.bottom) < T &&
                x >= selRect.left - T &&
                x <= selRect.right + T;
              const atSelLeft =
                nearLeft && Math.abs(rect.left - selRect.left) < T && y >= selRect.top - T && y <= selRect.bottom + T;
              const atSelRight =
                nearRight &&
                Math.abs(rect.right - selRect.right) < T &&
                y >= selRect.top - T &&
                y <= selRect.bottom + T;
              if (!atSelTop && !atSelBottom && !atSelLeft && !atSelRight) return false;
              const atSelCorner = (atSelTop || atSelBottom) && (atSelLeft || atSelRight);
              if (view.dom.classList.contains('resize-cursor') && !atSelCorner) return false;
              let anchorPos: number | null = null;
              if (sel instanceof CellSelection) {
                anchorPos = sel.$anchorCell.pos;
              } else {
                const ac = view.dom.querySelector('[data-active-cell]') as HTMLElement | null;
                if (ac) {
                  const pos = view.posAtDOM(ac, 0);
                  const $p = view.state.doc.resolve(pos);
                  for (let d = $p.depth; d > 0; d--) {
                    if ($p.node(d).type.name === 'tableCell' || $p.node(d).type.name === 'tableHeader') {
                      anchorPos = $p.before(d);
                      break;
                    }
                  }
                }
              }
              if (anchorPos === null) return false;
              event.preventDefault();
              view.dom.setAttribute('data-cell-selecting', '');

              const onMove = (ev: MouseEvent) => {
                const headPos = findCellAtCoords(view, ev.clientX, ev.clientY);
                if (headPos === null || anchorPos === null) return;
                try {
                  const $a = view.state.doc.resolve(anchorPos);
                  const $h = view.state.doc.resolve(headPos);
                  view.dispatch(view.state.tr.setSelection(CellSelection.create(view.state.doc, $a.pos, $h.pos)));
                } catch {
                  /* ignore */
                }
              };
              const cleanup = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                view.dom.removeAttribute('data-cell-selecting');
                clearCellDragCleanup(cleanup);
              };
              const onUp = () => cleanup();
              registerCellDragCleanup(cleanup);
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
              return true;
            },
          },
        },
        view(editorView) {
          let rafId = 0;
          let lastTop = -1;
          let lastLeft = -1;
          let lastWidth = -1;
          let lastHeight = -1;
          let lastMode = '';

          const scrollTarget = getEditorScrollContainer(editorView.dom) ?? document;

          const overlay = document.createElement('div');
          overlay.className = 'cell-selection-overlay';

          const dot = document.createElement('div');
          dot.className = 'cell-selection-dot';
          mountInEditorWrapper(editorView, [overlay, dot]);
          // Read on first reposition: a detached overlay computes ''.
          let baseOverlayRadius: string | null = null;

          dot.addEventListener('mousedown', e => {
            const sel = editorView.state.selection;
            let anchorPos: number | null = null;
            if (sel instanceof CellSelection) {
              anchorPos = sel.$anchorCell.pos;
            } else {
              const ac = editorView.dom.querySelector('[data-active-cell]') as HTMLElement | null;
              if (ac) {
                const pos = editorView.posAtDOM(ac, 0);
                const $p = editorView.state.doc.resolve(pos);
                for (let d = $p.depth; d > 0; d--) {
                  if ($p.node(d).type.name === 'tableCell' || $p.node(d).type.name === 'tableHeader') {
                    anchorPos = $p.before(d);
                    break;
                  }
                }
              }
            }
            if (anchorPos === null) return;
            e.preventDefault();
            e.stopPropagation();
            editorView.dom.setAttribute('data-cell-selecting', '');
            const onMove = (ev: MouseEvent) => {
              const headPos = findCellAtCoords(editorView, ev.clientX, ev.clientY);
              if (headPos === null || anchorPos === null) return;
              try {
                const $a = editorView.state.doc.resolve(anchorPos);
                const $h = editorView.state.doc.resolve(headPos);
                editorView.dispatch(
                  editorView.state.tr.setSelection(CellSelection.create(editorView.state.doc, $a.pos, $h.pos)),
                );
              } catch {
                /* ignore */
              }
            };
            const cleanup = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              editorView.dom.removeAttribute('data-cell-selecting');
              clearCellDragCleanup(cleanup);
            };
            const onUp = () => cleanup();
            registerCellDragCleanup(cleanup);
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          });

          const reposition = (toAbs: ToAbs, baseRadius: string) => {
            const sel = editorView.state.selection;
            const isCellSel = sel instanceof CellSelection;
            let r: DOMRect | null = null;
            let mode = 'none';

            if (isCellSel) {
              if (editorView.dom.querySelectorAll('.selectedCell').length === 0) return;
              r = getCellSelectionBounds(editorView);
              if (!r) return;
              mode = 'multi';
            } else {
              const activeCell = editorView.dom.querySelector('[data-active-cell]') as HTMLElement | null;
              if (!activeCell) {
                if (lastMode !== 'none') {
                  overlay.style.display = 'none';
                  dot.style.display = 'none';
                  lastMode = 'none';
                }
                return;
              }
              r = activeCell.getBoundingClientRect();
              mode = 'cell';
            }

            const same =
              mode === lastMode &&
              Math.abs(r.top - lastTop) < REPOSITION_TOLERANCE_PX &&
              Math.abs(r.left - lastLeft) < REPOSITION_TOLERANCE_PX &&
              Math.abs(r.width - lastWidth) < REPOSITION_TOLERANCE_PX &&
              Math.abs(r.height - lastHeight) < REPOSITION_TOLERANCE_PX;
            if (same) return;

            lastTop = r.top;
            lastLeft = r.left;
            lastWidth = r.width;
            lastHeight = r.height;
            lastMode = mode;

            const activeCell = editorView.dom.querySelector('[data-active-cell]') as HTMLElement | null;
            const cellWrapper = (activeCell || editorView.dom.querySelector('.selectedCell'))?.closest(
              '.tableWrapper',
            ) as HTMLElement | null;
            const wr = cellWrapper?.getBoundingClientRect();

            if (mode === 'multi') {
              const a = toAbs(r.left, r.top);
              overlay.style.display = 'block';
              overlay.style.top = `${a.top}px`;
              overlay.style.left = `${a.left}px`;
              overlay.style.width = `${r.width}px`;
              overlay.style.height = `${r.height}px`;
              overlay.style.borderRadius = selectionOverlayRadii(r, cellWrapper, baseRadius);
              if (wr) {
                const cl = Math.max(0, wr.left - r.left);
                const cr = Math.max(0, r.right - wr.right);
                overlay.style.clipPath = cl > 0 || cr > 0 ? `inset(0 ${cr}px 0 ${cl}px)` : '';
              }
            } else {
              overlay.style.display = 'none';
            }

            const dotRight = Math.min(r.right, wr ? wr.right : r.right);
            const da = toAbs(dotRight - DOT_HALF_PX, r.top + r.height / 2 - DOT_HALF_PX);
            dot.style.display = wr && r.right < wr.left ? 'none' : 'block';
            dot.style.top = `${da.top}px`;
            dot.style.left = `${da.left}px`;
          };

          const scheduleReposition = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
              const toAbs = mountInEditorWrapper(editorView, [overlay, dot]);
              if (!toAbs) return;
              baseOverlayRadius ??= getComputedStyle(overlay).borderRadius;
              reposition(toAbs, baseOverlayRadius);
            });
          };

          editorView.dom.addEventListener('mousemove', scheduleReposition);

          const onWrapperScroll = (e: Event) => {
            if ((e.target as HTMLElement)?.classList?.contains('tableWrapper')) {
              lastTop = -1;
              scheduleReposition();
            }
          };
          scrollTarget.addEventListener('scroll', onWrapperScroll, true);

          return {
            update() {
              scheduleReposition();
            },
            destroy() {
              cancelAnimationFrame(rafId);
              activeCellDragCleanup?.();
              activeCellDragCleanup = null;
              editorView.dom.removeEventListener('mousemove', scheduleReposition);
              scrollTarget.removeEventListener('scroll', onWrapperScroll, true);
              overlay.remove();
              dot.remove();
            },
          };
        },
      }),
      // Row/column controls: ··· above active column, ⋮ to left of active row
      new Plugin({
        key: new PluginKey('tableRowColControls'),
        view(editorView) {
          const scrollTarget = getEditorScrollContainer(editorView.dom) ?? document;

          const colCtrl = document.createElement('button');
          colCtrl.type = 'button';
          colCtrl.className = 'table-col-ctrl';

          const rowCtrl = document.createElement('button');
          rowCtrl.type = 'button';
          rowCtrl.className = 'table-row-ctrl';
          mountInEditorWrapper(editorView, [colCtrl, rowCtrl]);

          const dropdown = document.createElement('div');
          dropdown.className = 'table-ctrl-dropdown';
          dropdown.style.display = 'none';
          document.body.appendChild(dropdown);

          let menuOpen = false;
          let menuType: 'row' | 'col' | null = null;

          const hideMenu = () => {
            dropdown.style.display = 'none';
            menuOpen = false;
            menuType = null;
            colCtrl.classList.remove('active');
            rowCtrl.classList.remove('active');
          };

          const makeItem = (label: string, action: () => void, danger = false) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = `table-ctrl-menu-item${danger ? ' table-ctrl-menu-item-danger' : ''}`;
            btn.textContent = label;
            btn.addEventListener('mousedown', e => {
              e.preventDefault();
              e.stopPropagation();
              action();
              hideMenu();
            });
            return btn;
          };

          const showMenu = (type: 'row' | 'col', anchor: DOMRect) => {
            if (menuOpen && menuType === type) {
              hideMenu();
              return;
            }
            dropdown.innerHTML = '';
            menuType = type;
            menuOpen = true;

            const getCellIndex = () => {
              const cellEl = findActiveCellEl();
              if (!cellEl) return null;
              const row = cellEl.parentElement;
              const table = cellEl.closest('table');
              if (!row || !table) return null;
              const rows = Array.from(table.querySelectorAll('tr'));
              return {
                table,
                rowIdx: rows.indexOf(row as HTMLTableRowElement),
                colIdx: Array.from(row.children).indexOf(cellEl),
              };
            };

            const restoreCursor = (table: HTMLElement, rowIdx: number, colIdx: number) =>
              restoreCursorInTable(editor, table, rowIdx, colIdx);

            const addRowKeepCursor = (before: boolean) => {
              const info = getCellIndex();
              if (before) editor.chain().focus().addRowBefore().run();
              else editor.chain().focus().addRowAfter().run();
              if (info) restoreCursor(info.table, before ? info.rowIdx + 1 : info.rowIdx, info.colIdx);
            };

            const addColKeepCursor = (before: boolean) => {
              const info = getCellIndex();
              const wrapper = info?.table.closest('.tableWrapper') as HTMLElement | null;
              const savedWidths = wrapper ? captureColWidths(wrapper) : null;
              if (before) editor.chain().focus().addColumnBefore().run();
              else editor.chain().focus().addColumnAfter().run();
              if (savedWidths && info) {
                const insertIdx = before ? info.colIdx : info.colIdx + 1;
                const newWidths = [...savedWidths];
                newWidths.splice(insertIdx, 0, DEFAULT_NEW_COL_WIDTH_PX);
                applyColWidths(newWidths);
              }
              if (info) restoreCursor(info.table, info.rowIdx, before ? info.colIdx + 1 : info.colIdx);
            };

            if (type === 'row') {
              rowCtrl.classList.add('active');
              colCtrl.classList.remove('active');
              dropdown.appendChild(makeItem('Insert row above', () => addRowKeepCursor(true)));
              dropdown.appendChild(makeItem('Insert row below', () => addRowKeepCursor(false)));
              dropdown.appendChild(makeItem('Delete row', () => editor.chain().focus().deleteRow().run(), true));
            } else {
              colCtrl.classList.add('active');
              rowCtrl.classList.remove('active');
              dropdown.appendChild(makeItem('Insert column left', () => addColKeepCursor(true)));
              dropdown.appendChild(makeItem('Insert column right', () => addColKeepCursor(false)));
              dropdown.appendChild(makeItem('Delete column', () => editor.chain().focus().deleteColumn().run(), true));
            }

            if (type === 'col') {
              dropdown.style.top = `${anchor.bottom + 2}px`;
              dropdown.style.left = `${anchor.left + anchor.width / 2}px`;
              dropdown.style.transform = 'translateX(-50%)';
            } else {
              dropdown.style.top = `${anchor.bottom + 2}px`;
              dropdown.style.left = `${anchor.left}px`;
              dropdown.style.transform = '';
            }
            dropdown.style.display = 'block';
          };

          colCtrl.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            showMenu('col', colCtrl.getBoundingClientRect());
          });

          rowCtrl.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            showMenu('row', rowCtrl.getBoundingClientRect());
          });

          const onDocMousedown = (e: MouseEvent) => {
            if (
              menuOpen &&
              !dropdown.contains(e.target as Node) &&
              !colCtrl.contains(e.target as Node) &&
              !rowCtrl.contains(e.target as Node)
            ) {
              hideMenu();
            }
          };
          document.addEventListener('mousedown', onDocMousedown);

          let rafId = 0;

          const findActiveCellEl = (): HTMLElement | null => {
            const { selection } = editorView.state;
            if (selection instanceof CellSelection) {
              const dom = editorView.nodeDOM(selection.$headCell.pos);
              return dom instanceof HTMLElement ? dom : null;
            }
            const { $from } = selection;
            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
                const dom = editorView.nodeDOM($from.before(d));
                return dom instanceof HTMLElement ? dom : null;
              }
            }
            return null;
          };

          const reposition = (toAbs: ToAbs) => {
            const cellEl = findActiveCellEl();
            if (!cellEl) {
              if (!menuOpen) {
                colCtrl.classList.remove('visible');
                rowCtrl.classList.remove('visible');
              }
              return;
            }

            const table = cellEl.closest('table');
            const wrapper = cellEl.closest('.tableWrapper') as HTMLElement | null;
            if (!table) return;

            const cellRect = cellEl.getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            const wrapperRect = wrapper?.getBoundingClientRect();

            const colA = toAbs(cellRect.left, tableRect.top - 14);
            colCtrl.style.top = `${colA.top}px`;
            colCtrl.style.left = `${colA.left}px`;
            colCtrl.style.width = `${cellRect.width}px`;
            if (wrapperRect) {
              const clipLeft = Math.max(0, wrapperRect.left - cellRect.left);
              const clipRight = Math.max(0, cellRect.right - wrapperRect.right);
              colCtrl.style.clipPath = clipLeft > 0 || clipRight > 0 ? `inset(0 ${clipRight}px 0 ${clipLeft}px)` : '';
            } else {
              colCtrl.style.clipPath = '';
            }
            colCtrl.classList.add('visible');

            const rowLeft = tableRect.left - 14;
            const rowA = toAbs(rowLeft, cellRect.top);
            rowCtrl.style.top = `${rowA.top}px`;
            rowCtrl.style.left = `${rowA.left}px`;
            rowCtrl.style.height = `${cellRect.height}px`;
            if (wrapperRect && tableRect.left < wrapperRect.left) {
              const clipLeft = wrapperRect.left - rowLeft;
              rowCtrl.style.clipPath = `inset(0 0 0 ${clipLeft}px)`;
            } else {
              rowCtrl.style.clipPath = '';
            }
            rowCtrl.classList.add('visible');
          };

          const scheduleReposition = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
              const toAbs = mountInEditorWrapper(editorView, [colCtrl, rowCtrl]);
              if (toAbs) reposition(toAbs);
            });
          };

          const onWrapperScroll = (e: Event) => {
            if ((e.target as HTMLElement)?.classList?.contains('tableWrapper')) {
              scheduleReposition();
            }
          };
          scrollTarget.addEventListener('scroll', onWrapperScroll, true);

          editorView.dom.addEventListener('mousemove', scheduleReposition);

          return {
            update() {
              scheduleReposition();
            },
            destroy() {
              cancelAnimationFrame(rafId);
              hideMenu();
              editorView.dom.removeEventListener('mousemove', scheduleReposition);
              scrollTarget.removeEventListener('scroll', onWrapperScroll, true);
              colCtrl.remove();
              rowCtrl.remove();
              dropdown.remove();
              document.removeEventListener('mousedown', onDocMousedown);
            },
          };
        },
      }),
    ];
  },
});
